import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import Button from '../general/Button';
import Switch from './Switch';
import { color_defaults_white, color_shades_dark, color_text_light, dark_grey_blue, cool_grey, pale_grey } from '../../constants/colors';
import ApplicationModel from '../../models/applications/api/ApplicationModel';

const calendarIcon = require("../../content/images/analytics-icons/calendar.svg");

interface OutOfStockTimeWindowProps {
    startDate?: string;
    endDate?: string;
    isEnabled?: boolean;
    onChange: (startDate?: string, endDate?: string, isEnabled?: boolean) => void;
    onClose?: () => void;
    className?: string;
    application: ApplicationModel
}

const OutOfStockTimeWindow: React.FC<OutOfStockTimeWindowProps> = (props) => {
    const timeZone = props?.application?.timeZone ?? "America/New_York";
    const [startDate, setStartDate] = useState<string>(props.startDate || moment().tz(timeZone).format('YYYY-MM-DDTHH:mm'));
    const [endDate, setEndDate] = useState<string>(props.endDate || moment().tz(timeZone).add(1, 'day').format('YYYY-MM-DDTHH:mm'));
    const [isEnabled, setIsEnabled] = useState<boolean>(props.isEnabled || false);


    useEffect(() => {
        let startDate = props.startDate;
        let endDate = props.endDate;
        if (props.startDate) {
            startDate = moment(props.startDate).tz(timeZone).format('YYYY-MM-DDTHH:mm');
        }
        if (props.endDate) {
            endDate = moment(props.endDate).tz(timeZone).format('YYYY-MM-DDTHH:mm');
        }
        setStartDate(startDate || moment().tz(timeZone).format('YYYY-MM-DDTHH:mm'));
        setEndDate(endDate || moment().tz(timeZone).add(1, 'day').format('YYYY-MM-DDTHH:mm'));
        setIsEnabled(props.isEnabled || false);
    }, [props.startDate, props.endDate, props.isEnabled, props.application]);

    const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newStartDate = e.target.value;
        setStartDate(newStartDate);
        setEndDate(currentEndDate => {
            let finalEndDate = currentEndDate;

            if (moment(newStartDate).tz(timeZone).isAfter(moment(currentEndDate).tz(timeZone))) {
                finalEndDate = moment(newStartDate).tz(timeZone).add(1, 'hour').format('YYYY-MM-DDTHH:mm');
            }

            //convert to UTC before sending to parent componet
            const utcStartDate = moment.tz(newStartDate, timeZone).utc().toISOString();
            const utcEndDate = moment.tz(finalEndDate, timeZone).utc().toISOString();
            props.onChange(utcStartDate, utcEndDate, isEnabled);
            return finalEndDate;
        });
    };

    const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newEndDate = e.target.value;
        setEndDate(newEndDate);

        //convert to UTC before sending to parent componet
        const utcStartDate = moment.tz(startDate, timeZone).utc().toISOString();
        const utcEndDate = moment.tz(newEndDate, timeZone).utc().toISOString();
        props.onChange(utcStartDate, utcEndDate, isEnabled);
    };

    const handleEnabledChange = () => {
        setIsEnabled(!isEnabled);

        //convert to UTC before sending to parent componet
        const utcStartDate = moment.tz(startDate, timeZone).utc().toISOString();
        const utcEndDate = moment.tz(endDate, timeZone).utc().toISOString();
        props.onChange(utcStartDate, utcEndDate, !isEnabled);
    };

    const clearTimeWindow = () => {
        setStartDate(moment().tz(timeZone).format('YYYY-MM-DDTHH:mm'));
        setEndDate(moment().tz(timeZone).add(1, 'day').format('YYYY-MM-DDTHH:mm'));
        setIsEnabled(false);
        props.onChange(undefined, undefined, false);
    };

    const formatDateTimeForDisplay = (dateTime: string) => {
        return moment(dateTime).tz(timeZone).format('MMM DD, YYYY h:mm A');
    };

    const renderTimeZone = () => {
        return `${props.application.timeZone ? "Location's" : "Your"} time zone: ${timeZone}`;
    }

    return (
        <Container className={props.className}>
            <Header>
                <HeaderTitle>Out of Stock Time Window ({renderTimeZone()})</HeaderTitle>
                <Switch
                    checked={isEnabled}
                    onChange={handleEnabledChange}
                    detail={isEnabled ? "Enabled" : "Disabled"}
                    stopPropagation={true}
                />
            </Header>

            {isEnabled && (
                <DateFieldsContainer>
                    <DateFieldWrapper>
                        <DateFieldLabel>Start Date & Time</DateFieldLabel>
                        <DateInputContainer>
                            <img src={calendarIcon} />
                            <DateInput
                                type="datetime-local"
                                value={startDate}
                                onChange={handleStartDateChange}
                            />
                        </DateInputContainer>
                    </DateFieldWrapper>
                    <DateFieldWrapper>
                        <DateFieldLabel>End Date & Time</DateFieldLabel>
                        <DateInputContainer>
                            <img src={calendarIcon} />
                            <DateInput
                                type="datetime-local"
                                value={endDate}
                                onChange={handleEndDateChange}
                            />
                        </DateInputContainer>
                    </DateFieldWrapper>

                    <SummaryContainer>
                        <SummaryText>
                            Item will be unavailable from <strong>{formatDateTimeForDisplay(startDate)}</strong> to <strong>{formatDateTimeForDisplay(endDate)} - {timeZone}</strong>
                        </SummaryText>
                        <Button
                            themes={['secondary-small']}
                            text="Clear"
                            onClick={clearTimeWindow}
                        />
                    </SummaryContainer>
                </DateFieldsContainer>
            )}
        </Container>
    );
};

const Container = styled.div`
    background: ${color_defaults_white};
    border: 1px solid ${color_shades_dark};
    border-radius: 8px;
    padding: 16px;
    margin-top: 16px;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
`;

const HeaderTitle = styled.h4`
    margin: 0;
    font-weight: 600;
    font-size: 14px;
`;

const DateFieldsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const DateFieldWrapper = styled.div`
    display: flex;
    flex-direction: column;
`;

const SummaryContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px;
    background: #f8f9fa;
    border-radius: 4px;
    margin-top: 8px;
`;

const SummaryText = styled.span`
    font-size: 12px;
    color: ${color_text_light};
    flex: 1;
`;

const DateFieldLabel = styled.label`
    font-family: Muli;
    font-size: 14px;
    font-weight: 600;
    text-align: left;
    margin-bottom: 8px;
    display: block;
`;

const DateInputContainer = styled.div`
    background: #FFFFFF;
    border: 1px solid ${color_shades_dark};
    box-sizing: border-box;
    box-shadow: inset 0px 1px 2px rgba(50, 70, 97, 0.1);
    border-radius: 8px;
    display: flex;
    align-items: center;
    padding: 0 8px;
    height: 60px;
    width: 100%;
    position: relative;
    cursor: pointer;

    > img {
        width: 20px;
        height: 20px;
        margin-right: 12px;
        pointer-events: none;
    }
`;

const DateInput = styled.input`
    border: none;
    background: transparent;
    box-shadow: none;
    font-family: Muli;
    font-style: normal;
    font-weight: normal;
    font-size: 14px;
    line-height: 20px;
    color: ${dark_grey_blue};
    width: 100%;
    cursor: pointer;

    &::-webkit-calendar-picker-indicator {
        position: absolute;
        left: 0;
        right: 0;
        width: 100%;
        height: 100%;
        margin: 0;
        padding: 0;
        cursor: pointer;
        opacity: 0;
    }

    &::-webkit-inner-spin-button {
        display: none;
    }

    ::placeholder {
        font-weight: normal;
        font-style: italic;
        letter-spacing: normal;
        color: ${cool_grey};
    }

    &:disabled {
        background: ${pale_grey};
    }
`;

export default OutOfStockTimeWindow; 